import type * as router from '@attain-apps/fe/web/main/lib/router'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Feature } from '../feature'
import * as models from '../models'

import { waitForPaint$ } from './wait-for-paint'

/**
 * Executes feature-owned navigation after the transition overlay has painted.
 */
export const executeNavigate = store.effects.create(
  (feature: Feature, routerFeature: router.Router) =>
    feature.actions.navigate.observe().pipe(
      rxjs.withLatestFrom(routerFeature.selectors.rawUrl.asObservable),
      rxjs.concatMap(([action, currentUrl]) => {
        // Keep router navigation creation lazy so both branches preserve the
        // original action source, but only create the action when it will emit.
        const routerAction = () =>
          routerFeature.actions.navigate.from(action).create(action.data)

        if (
          !models.AppTransition.shouldShow({
            currentUrl,
            request: action.data,
            routerFeature,
          })
        ) {
          return rxjs.of(routerAction())
        }

        // Cross-app navigation can synchronously destroy/create a large route
        // injector tree. Emit the transition first, wait for it to paint, then
        // let Angular start the expensive navigation work.
        return rxjs.concat(
          rxjs.of(feature.actions.startTransition.from(action).create()),
          waitForPaint$().pipe(rxjs.map(routerAction)),
        )
      }),
    ),
)
